// pages/Invoice.js - Updated with Manual Rounding

import { useState, useEffect } from "react";
import { database } from "../firebase/firebase";
import { ref, push, onValue, get, update, set } from "firebase/database";
import { useNavigate, useParams } from "react-router-dom";
import "../styles/main.css";
import BarcodeScanner from "../components/BarcodeScanner";
import PrintService from '../components/PrintService';

// Configuration
const FINANCIAL_YEAR_START_MONTH = 3; // April (0-indexed)

export default function Invoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  
  // Initialize print service
  const printService = new PrintService();

  // State for form fields
  const [customer, setCustomer] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showItemDropdown, setShowItemDropdown] = useState(false);
  const [itemSearch, setItemSearch] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [roundOff, setRoundOff] = useState(false);
  const [manualRoundOff, setManualRoundOff] = useState(null);
  const [currentItem, setCurrentItem] = useState({
    id: "",
    name: "",
    qty: 1,
    price: 0,
    gst: 0,
    discount: 0,
    hsn: ""
  });
  const [notes, setNotes] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [manualInvoiceNo, setManualInvoiceNo] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [financialYear, setFinancialYear] = useState("");
  const [invoiceNumberError, setInvoiceNumberError] = useState("");
  const [existingInvoiceNumbers, setExistingInvoiceNumbers] = useState(new Set());
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Date state
  const [invoiceDate, setInvoiceDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });

  // Company details from Firebase
  const [companyDetails, setCompanyDetails] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    gstin: "",
    website: "",
    logo: "",
    state: "",
    stateCode: "",
    terms: "Thank you for your business!"
  });

  // Bank Details from Firebase
  const [bankDetails, setBankDetails] = useState({
    bankName: "",
    accountNo: "",
    ifsc: "",
    holderName: "",
    branch: ""
  });

  // UPI Details from Firebase
  const [upiDetails, setUpiDetails] = useState({
    upiId: "",
    upiApp: "",
    showQR: true
  });

  // Settings from Firebase
  const [settings, setSettings] = useState({
    financialYear: "",
    pdfShareOption: "share",
    printFormat: "standard",
    billFormat: "standard",
    notificationEnabled: true,
    darkMode: false,
    autoBackup: true,
    billSize: "a4",
    invoicePrefix: "INV-",
    invoiceSuffix: "",
    invoiceStartingNumber: 1,
    allowManualInvoiceNumber: true,
    showUPIQR: true,
    enableRoundOff: false
  });

  // Utility function to get current financial year
  const getFinancialYear = (date = new Date()) => {
    if (settings.financialYear && settings.financialYear !== "") {
      return settings.financialYear;
    }
    const year = date.getFullYear();
    const month = date.getMonth();
    if (month < FINANCIAL_YEAR_START_MONTH) {
      return `${year-1}-${year}`;
    }
    return `${year}-${year+1}`;
  };

  // Manual round off function - YOU control it manually
  const calculateRoundOff = (amount) => {
    if (!roundOff) return { originalAmount: amount, roundedAmount: amount, roundOff: 0 };
    
    // Get decimal part
    const decimal = amount - Math.floor(amount);
    let roundedAmount;
    let roundOffValue;
    
    // Manual rounding based on decimal value
    if (decimal <= 0.50) {
      // If decimal is 0.50 or less, round DOWN (minus)
      roundedAmount = Math.floor(amount);
      roundOffValue = roundedAmount - amount;
    } else {
      // If decimal is above 0.50, round UP (plus)
      roundedAmount = Math.ceil(amount);
      roundOffValue = roundedAmount - amount;
    }
    
    return {
      originalAmount: amount,
      roundedAmount: roundedAmount,
      roundOff: roundOffValue
    };
  };

  // Handle manual round off input
  const handleManualRoundOffChange = (value) => {
    if (value === "") {
      setManualRoundOff(null);
      return;
    }
    const num = parseFloat(value);
    if (!isNaN(num)) {
      setManualRoundOff(num);
    }
  };

  // Update financial year
  useEffect(() => {
    if (settings.financialYear && settings.financialYear !== "") {
      setFinancialYear(settings.financialYear);
    } else {
      const selectedDate = new Date(invoiceDate);
      const fy = getFinancialYear(selectedDate);
      setFinancialYear(fy);
    }
  }, [invoiceDate, settings.financialYear]);

  // Load settings for rounding
  useEffect(() => {
    if (settings.enableRoundOff !== undefined) {
      setRoundOff(settings.enableRoundOff);
    }
  }, [settings]);

  // Load existing invoice numbers
  const loadExistingInvoiceNumbers = async (fy) => {
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(invoiceDate));
      const financialYearsRef = ref(database, `financialYears/${currentFY}/bills`);
      const snapshot = await get(financialYearsRef);
      const numbers = new Set();
      let lastNum = 0;
      
      if (snapshot.exists()) {
        const bills = snapshot.val();
        Object.values(bills).forEach(bill => {
          if (bill.invoiceNo) {
            numbers.add(bill.invoiceNo);
            const match = bill.invoiceNo.match(/\d+/);
            if (match) {
              const num = parseInt(match[0], 10);
              if (!isNaN(num) && num > lastNum) lastNum = num;
            }
          }
        });
      }
      
      setExistingInvoiceNumbers(numbers);
      return { numbers, lastNumber: lastNum };
    } catch (error) {
      console.error("Error loading invoice numbers:", error);
      return { numbers: new Set(), lastNumber: 0 };
    }
  };

  // Generate invoice number
  const generateInvoiceNumber = async (fy, forceGenerate = false) => {
    try {
      const currentFY = fy || settings.financialYear || getFinancialYear(new Date(invoiceDate));
      const prefix = settings.invoicePrefix || 'INV-';
      const { lastNumber } = await loadExistingInvoiceNumbers(currentFY);
      
      let newNumber;
      if (lastNumber > 0) {
        newNumber = lastNumber + 1;
      } else {
        newNumber = 1;
      }
      
      if (!isEditMode || forceGenerate) {
        setInvoiceNumber(newNumber);
        const newInvoiceNo = `${prefix}${newNumber}`;
        setInvoiceNo(newInvoiceNo);
        setManualInvoiceNo(newInvoiceNo);
        setInvoiceNumberError("");
      }
      
      return newNumber;
    } catch (error) {
      console.error("Error generating invoice number:", error);
      const fallbackNumber = 1;
      if (!isEditMode || forceGenerate) {
        setInvoiceNumber(fallbackNumber);
        setInvoiceNo(`${settings.invoicePrefix || 'INV-'}${fallbackNumber}`);
        setManualInvoiceNo(`${settings.invoicePrefix || 'INV-'}${fallbackNumber}`);
      }
      return fallbackNumber;
    }
  };

  const isInvoiceNumberDuplicate = (number) => {
    if (isEditMode) {
      return existingInvoiceNumbers.has(number) && number !== invoiceNo;
    }
    return existingInvoiceNumbers.has(number);
  };

  const handleManualInvoiceNoChange = (e) => {
    const value = e.target.value;
    setManualInvoiceNo(value);
    
    const match = value.match(/\d+/);
    if (match) {
      setInvoiceNumber(parseInt(match[0], 10));
    }
    
    if (isInvoiceNumberDuplicate(value)) {
      setInvoiceNumberError("This invoice number already exists. Please use a different number.");
    } else {
      setInvoiceNumberError("");
    }
  };

  const applyManualInvoiceNo = () => {
    if (!manualInvoiceNo.trim()) {
      alert("Please enter an invoice number");
      return;
    }
    
    if (isInvoiceNumberDuplicate(manualInvoiceNo)) {
      alert("This invoice number already exists. Please use a different number.");
      return;
    }
    
    setInvoiceNo(manualInvoiceNo);
    setInvoiceNumberError("");
  };

  const handleInvoiceNumberChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    setInvoiceNumber(value);
    const newInvoiceNo = `${settings.invoicePrefix || 'INV-'}${value}`;
    setInvoiceNo(newInvoiceNo);
    setManualInvoiceNo(newInvoiceNo);
    
    if (isInvoiceNumberDuplicate(newInvoiceNo)) {
      setInvoiceNumberError("This invoice number already exists. Please use a different number.");
    } else {
      setInvoiceNumberError("");
    }
  };

  const handleBarcodeScan = (barcode) => {
    const foundItem = items.find(item => item.barcode === barcode || item.code === barcode);
    if (foundItem) {
      addItemWithDetails({
        id: foundItem.id,
        name: foundItem.name,
        qty: 1,
        price: Number(foundItem.sellingPrice || foundItem.price || 0),
        gst: Number(foundItem.gst || 0),
        discount: 0,
        hsn: foundItem.hsn || ""
      });
      setShowScanner(false);
    } else {
      alert("Item not found with this barcode");
    }
  };

  const handleItemSelect = (item) => {
    if (item) {
      addItemWithDetails({
        id: item.id,
        name: item.name,
        qty: 1,
        price: Number(item.sellingPrice || item.price || 0),
        gst: Number(item.gst || 0),
        discount: 0,
        hsn: item.hsn || ""
      });
      setItemSearch("");
      setShowItemDropdown(false);
    }
  };

  // FIXED: Use actual item ID and handle duplicate items
  const addItemWithDetails = (itemDetails) => {
    const qty = Number(itemDetails.qty) || 1;
    const price = Number(itemDetails.price) || 0;
    const gst = Number(itemDetails.gst) || 0;
    const discount = Number(itemDetails.discount) || 0;

    const itemTotal = qty * price;
    const discountAmount = itemTotal * (discount / 100);
    const taxableValue = itemTotal - discountAmount;
    
    const gstDetails = calculateGST(taxableValue, gst, selectedCustomer?.state);
    const netTotal = taxableValue + gstDetails.total;

    // Check if item already exists in invoice items
    const existingItemIndex = invoiceItems.findIndex(item => item.id === itemDetails.id);
    
    if (existingItemIndex >= 0) {
      // Update existing item quantity
      const updatedItems = [...invoiceItems];
      const existingItem = updatedItems[existingItemIndex];
      const newQty = existingItem.qty + qty;
      
      const newItemTotal = newQty * price;
      const newDiscountAmount = newItemTotal * (discount / 100);
      const newTaxableValue = newItemTotal - newDiscountAmount;
      
      const newGstDetails = calculateGST(newTaxableValue, gst, selectedCustomer?.state);
      const newNetTotal = newTaxableValue + newGstDetails.total;
      
      updatedItems[existingItemIndex] = {
        ...existingItem,
        qty: newQty,
        itemTotal: newItemTotal,
        discountAmount: newDiscountAmount,
        taxableValue: newTaxableValue,
        gstDetails: newGstDetails,
        netTotal: newNetTotal
      };
      
      setInvoiceItems(updatedItems);
    } else {
      // Add new item - use the actual item ID from database
      setInvoiceItems([
        ...invoiceItems,
        {
          ...itemDetails,
          qty,
          price,
          gst,
          discount,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal,
          id: itemDetails.id,
          itemId: itemDetails.id
        }
      ]);
    }
  };

  const calculateGST = (itemTotal, gstRate, customerState) => {
    const isSameState = customerState?.toLowerCase() === companyDetails.state?.toLowerCase();
    const gstAmount = itemTotal * (gstRate / 100);
    
    if (isSameState) {
      return {
        cgst: gstAmount / 2,
        sgst: gstAmount / 2,
        igst: 0,
        total: gstAmount
      };
    } else {
      return {
        cgst: 0,
        sgst: 0,
        igst: gstAmount,
        total: gstAmount
      };
    }
  };

  const removeItem = (itemId) => {
    setInvoiceItems(invoiceItems.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId, newQty) => {
    if (newQty < 1) return;
    
    setInvoiceItems(invoiceItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(newQty) || 1;
        const price = Number(item.price) || 0;
        const gst = Number(item.gst) || 0;
        const discount = Number(item.discount) || 0;
        
        const itemTotal = qty * price;
        const discountAmount = itemTotal * (discount / 100);
        const taxableValue = itemTotal - discountAmount;
        
        const gstDetails = calculateGST(taxableValue, gst, selectedCustomer?.state);
        const netTotal = taxableValue + gstDetails.total;
        
        return {
          ...item,
          qty,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const updatePrice = (itemId, newPrice) => {
    setInvoiceItems(invoiceItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(item.qty) || 1;
        const price = Number(newPrice) || 0;
        const gst = Number(item.gst) || 0;
        const discount = Number(item.discount) || 0;
        
        const itemTotal = qty * price;
        const discountAmount = itemTotal * (discount / 100);
        const taxableValue = itemTotal - discountAmount;
        
        const gstDetails = calculateGST(taxableValue, gst, selectedCustomer?.state);
        const netTotal = taxableValue + gstDetails.total;
        
        return {
          ...item,
          price,
          itemTotal,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const updateDiscount = (itemId, discount) => {
    setInvoiceItems(invoiceItems.map(item => {
      if (item.id === itemId) {
        const qty = Number(item.qty) || 1;
        const price = Number(item.price) || 0;
        const gst = Number(item.gst) || 0;
        const newDiscount = Number(discount) || 0;
        
        const itemTotal = qty * price;
        const discountAmount = itemTotal * (newDiscount / 100);
        const taxableValue = itemTotal - discountAmount;
        
        const gstDetails = calculateGST(taxableValue, gst, selectedCustomer?.state);
        const netTotal = taxableValue + gstDetails.total;
        
        return {
          ...item,
          discount: newDiscount,
          discountAmount,
          taxableValue,
          gstDetails,
          netTotal
        };
      }
      return item;
    }));
  };

  const calculateTotals = () => {
    let taxableValue = 0;
    let cgst = 0;
    let sgst = 0;
    let igst = 0;
    let totalGST = 0;
    let totalDiscount = 0;
    let grandTotal = 0;

    invoiceItems.forEach(item => {
      taxableValue += Number(item.taxableValue) || 0;
      cgst += Number(item.gstDetails?.cgst) || 0;
      sgst += Number(item.gstDetails?.sgst) || 0;
      igst += Number(item.gstDetails?.igst) || 0;
      totalGST += Number(item.gstDetails?.total) || 0;
      totalDiscount += Number(item.discountAmount) || 0;
      grandTotal += Number(item.netTotal) || 0;
    });

    // Apply manual rounding
    let roundedGrandTotal = grandTotal;
    let roundOffAmount = 0;
    
    if (roundOff) {
      // Use manual round off if set, otherwise auto-calculate
      if (manualRoundOff !== null && !isNaN(manualRoundOff)) {
        roundOffAmount = manualRoundOff;
        roundedGrandTotal = grandTotal + roundOffAmount;
      } else {
        const rounding = calculateRoundOff(grandTotal);
        roundedGrandTotal = rounding.roundedAmount;
        roundOffAmount = rounding.roundOff;
      }
    }

    return {
      taxableValue,
      cgst,
      sgst,
      igst,
      totalGST,
      totalDiscount,
      grandTotal,
      roundedGrandTotal,
      roundOffAmount,
      showRoundOff: roundOff
    };
  };

  const filteredCustomers = customers.filter(c => 
    c.name?.toLowerCase().includes(customer.toLowerCase()) ||
    c.mobile?.includes(customer) ||
    c.customerId?.toLowerCase().includes(customer.toLowerCase())
  );

  const filteredItems = items.filter(item => 
    item.name?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.hsn?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    item.barcode?.includes(itemSearch) ||
    item.code?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust);
    setCustomer(cust.name);
    setCustomerId(cust.customerId);
    setShowCustomerDropdown(false);
    
    setInvoiceItems(prevItems => 
      prevItems.map(item => {
        const gstDetails = calculateGST(item.taxableValue, item.gst, cust.state);
        return {
          ...item,
          gstDetails,
          netTotal: item.taxableValue + gstDetails.total
        };
      })
    );
  };

  // Update stock function - DEDUCTS stock when invoice is saved
  const updateStockForSale = async (itemId, quantity) => {
    try {
      const itemRef = ref(database, `items/${itemId}`);
      const itemSnapshot = await get(itemRef);
      
      if (itemSnapshot.exists()) {
        const item = itemSnapshot.val();
        const currentStock = item.currentStock || 0;
        
        // Check if sufficient stock is available
        if (currentStock < quantity) {
          throw new Error(`Insufficient stock for ${item.name}. Available: ${currentStock}, Required: ${quantity}`);
        }
        
        const newStock = currentStock - quantity;
        
        await update(itemRef, {
          currentStock: newStock,
          lastSaleDate: invoiceDate,
          lastSaleQuantity: quantity,
          lastStockUpdate: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        
        console.log(`📦 Stock DECREASED for ${item.name}: ${currentStock} → ${newStock} (-${quantity})`);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating stock:", error);
      throw error;
    }
  };

  // Direct print without opening new tab
  const handleDirectPrint = () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }

    if (invoiceItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    const totals = calculateTotals();
    
    const printData = {
      settings,
      company: companyDetails,
      selectedCustomer,
      invoiceItems,
      totals,
      invoiceNo,
      invoiceDate,
      dueDate: invoiceDate,
      notes,
      terms: companyDetails.terms || 'Thank you for your business!',
      bankDetails,
      upiDetails
    };

    printService.print(printData);
  };

  const handlePreview = () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }

    if (invoiceItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    const totals = calculateTotals();
    
    const printData = {
      settings,
      company: companyDetails,
      selectedCustomer,
      invoiceItems,
      totals,
      invoiceNo,
      invoiceDate,
      dueDate: invoiceDate,
      notes,
      terms: companyDetails.terms || 'Thank you for your business!',
      bankDetails,
      upiDetails
    };

    printService.preview(printData);
  };

  const updateFinancialYearSummary = async (fy, invoiceData) => {
    try {
      const summaryRef = ref(database, `financialYears/${fy}/summary`);
      const snapshot = await get(summaryRef);
      
      const currentSummary = snapshot.exists() ? snapshot.val() : {
        totalInvoices: 0,
        totalRevenue: 0,
        totalTax: 0,
        totalDiscounts: 0
      };
      
      const updatedSummary = {
        totalInvoices: (currentSummary.totalInvoices || 0) + 1,
        totalRevenue: (currentSummary.totalRevenue || 0) + (invoiceData.grandTotal || 0),
        totalTax: (currentSummary.totalTax || 0) + (invoiceData.totalGST || 0),
        totalDiscounts: (currentSummary.totalDiscounts || 0) + (invoiceData.totalDiscount || 0),
        lastUpdated: new Date().toISOString()
      };
      
      await set(summaryRef, updatedSummary);
    } catch (error) {
      console.error("Error updating financial year summary:", error);
    }
  };

  const cleanData = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(item => cleanData(item));
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        if (value !== undefined && value !== null) {
          if (typeof value === 'object') {
            acc[key] = cleanData(value);
          } else {
            acc[key] = value;
          }
        }
        return acc;
      }, {});
    }
    return obj;
  };

  const resetForm = () => {
    setSelectedCustomer(null);
    setCustomer("");
    setCustomerId("");
    setInvoiceItems([]);
    setNotes("");
    setInvoiceNumberError("");
    setManualRoundOff(null);
    generateInvoiceNumber(settings.financialYear || getFinancialYear(new Date(invoiceDate)), true);
  };

  const saveInvoice = async () => {
    if (!selectedCustomer) {
      alert("Please select a customer");
      return;
    }

    if (invoiceItems.length === 0) {
      alert("Please add at least one item");
      return;
    }

    if (isInvoiceNumberDuplicate(invoiceNo)) {
      alert("This invoice number already exists. Please use a different number.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (!database) {
        throw new Error("Database connection not available");
      }

      const timestamp = new Date().toISOString();
      const totals = calculateTotals();
      const fy = settings.financialYear || getFinancialYear(new Date(invoiceDate));
      
      const invoiceData = {
        invoiceNo: invoiceNo || "",
        financialYear: fy,
        companyDetails: {
          name: companyDetails?.name || "",
          address: companyDetails?.address || "",
          phone: companyDetails?.phone || "",
          email: companyDetails?.email || "",
          gstin: companyDetails?.gstin || "",
          website: companyDetails?.website || "",
          state: companyDetails?.state || "",
          stateCode: companyDetails?.stateCode || "",
          terms: companyDetails?.terms || "Thank you for your business!"
        },
        customerId: selectedCustomer?.id || "",
        customerName: selectedCustomer?.name || "",
        customerMobile: selectedCustomer?.mobile || "",
        customerGST: selectedCustomer?.gst || "",
        customerAddress: selectedCustomer?.address || "",
        customerState: selectedCustomer?.state || "",
        customerStateCode: selectedCustomer?.stateCode || "",
        items: (invoiceItems || []).map(item => ({
          itemId: item?.id || "",
          name: item?.name || "",
          hsn: item?.hsn || "",
          qty: Number(item?.qty) || 0,
          price: Number(item?.price) || 0,
          gst: Number(item?.gst) || 0,
          discount: Number(item?.discount) || 0,
          itemTotal: Number(item?.itemTotal) || 0,
          discountAmount: Number(item?.discountAmount) || 0,
          taxableValue: Number(item?.taxableValue) || 0,
          gstDetails: {
            cgst: Number(item?.gstDetails?.cgst) || 0,
            sgst: Number(item?.gstDetails?.sgst) || 0,
            igst: Number(item?.gstDetails?.igst) || 0,
            total: Number(item?.gstDetails?.total) || 0
          },
          netTotal: Number(item?.netTotal) || 0
        })),
        taxableValue: Number(totals.taxableValue) || 0,
        cgst: Number(totals.cgst) || 0,
        sgst: Number(totals.sgst) || 0,
        igst: Number(totals.igst) || 0,
        totalGST: Number(totals.totalGST) || 0,
        totalDiscount: Number(totals.totalDiscount) || 0,
        grandTotal: Number(totals.grandTotal) || 0,
        roundedGrandTotal: Number(totals.roundedGrandTotal) || totals.grandTotal,
        roundOffAmount: Number(totals.roundOffAmount) || 0,
        showRoundOff: totals.showRoundOff || false,
        manualRoundOff: manualRoundOff || null,
        notes: notes || "",
        date: invoiceDate || new Date().toISOString().split('T')[0],
        createdAt: isEditMode ? null : timestamp,
        updatedAt: timestamp,
        status: "completed",
        billSize: settings?.billSize || "a4",
        printFormat: settings?.printFormat || "standard",
        billFormat: settings?.billFormat || "standard"
      };

      const cleanedInvoiceData = cleanData(invoiceData);

      if (isEditMode) {
        // Update existing invoice
        const financialYearsRef = ref(database, "financialYears");
        const snapshot = await get(financialYearsRef);
        
        if (snapshot.exists()) {
          const years = snapshot.val();
          let foundYear = null;
          
          for (const [year, yearData] of Object.entries(years)) {
            if (yearData.bills && yearData.bills[id]) {
              foundYear = year;
              break;
            }
          }
          
          if (foundYear) {
            const invoiceRef = ref(database, `financialYears/${foundYear}/bills/${id}`);
            await update(invoiceRef, {
              ...cleanedInvoiceData,
              updatedAt: timestamp
            });
          } else {
            const billsRef = ref(database, `financialYears/${fy}/bills`);
            const billsSnapshot = await get(billsRef);
            if (!billsSnapshot.exists()) {
              await set(billsRef, {});
            }
            const invoiceRef = ref(database, `financialYears/${fy}/bills/${id}`);
            await set(invoiceRef, {
              ...cleanedInvoiceData,
              updatedAt: timestamp
            });
          }
        }
        alert(`Invoice ${invoiceNo} updated successfully!`);
        setTimeout(() => {
          navigate("/invoicelist");
        }, 1500);
      } else {
        // Create new invoice
        const financialYearRef = ref(database, `financialYears/${fy}`);
        const fySnapshot = await get(financialYearRef);
        
        if (!fySnapshot.exists()) {
          await set(financialYearRef, {
            bills: {},
            summary: {
              totalInvoices: 0,
              totalRevenue: 0,
              totalTax: 0,
              totalDiscounts: 0,
              lastUpdated: timestamp
            }
          });
        } else {
          const billsRef = ref(database, `financialYears/${fy}/bills`);
          const billsSnapshot = await get(billsRef);
          if (!billsSnapshot.exists()) {
            await set(billsRef, {});
          }
        }
        
        // Update stock for each item BEFORE saving invoice
        for (const item of invoiceItems) {
          await updateStockForSale(item.id, item.qty);
        }
        
        const billsRef = ref(database, `financialYears/${fy}/bills`);
        await push(billsRef, cleanedInvoiceData);
        await updateFinancialYearSummary(fy, cleanedInvoiceData);
        
        alert(`Invoice ${invoiceNo} saved successfully! Stock has been updated.`);
        resetForm();
        await generateInvoiceNumber(fy, true);
        setLoading(false);
      }

    } catch (error) {
      console.error("Error saving invoice:", error);
      setError(error.message || "Failed to save invoice");
      alert(`Failed to save invoice: ${error.message}`);
      setLoading(false);
    }
  };

  // Fetch data from Firebase
  useEffect(() => {
    const initializeData = async () => {
      const settingsRef = ref(database, "settings");
      const settingsSnapshot = await get(settingsRef);
      let settingsData = {};
      
      if (settingsSnapshot.exists()) {
        settingsData = settingsSnapshot.val();
        setSettings(prev => ({
          ...prev,
          ...settingsData,
          financialYear: settingsData.financialYear || "2025-26",
          billSize: settingsData.billSize || settingsData.printSize || "a4",
          printFormat: settingsData.printFormat || "standard",
          billFormat: settingsData.billFormat || "standard",
          invoicePrefix: settingsData.invoicePrefix || "INV-",
          invoiceStartingNumber: settingsData.invoiceStartingNumber || 1,
          allowManualInvoiceNumber: settingsData.allowManualInvoiceNumber !== false,
          showUPIQR: settingsData.showUPIQR !== false,
          enableRoundOff: settingsData.enableRoundOff || false
        }));
        setRoundOff(settingsData.enableRoundOff || false);
      }

      const companyRef = ref(database, "company");
      onValue(companyRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setCompanyDetails(prev => ({
            ...prev,
            ...data,
            terms: data.terms || "Thank you for your business!"
          }));
        }
      });

      const bankRef = ref(database, "bankDetails");
      onValue(bankRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setBankDetails(data);
        }
      });

      const upiRef = ref(database, "upiDetails");
      onValue(upiRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          setUpiDetails(data);
        }
      });

      const itemsRef = ref(database, "items");
      onValue(itemsRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const itemList = Object.entries(data).map(([id, item]) => ({
            id,
            ...item,
            sellingPrice: Number(item.sellingPrice) || 0,
            price: Number(item.price) || 0,
            gst: Number(item.gst) || 0,
            hsn: item.hsn || "",
            name: item.name || "",
            barcode: item.barcode || "",
            code: item.code || "",
            currentStock: item.currentStock || 0
          }));
          setItems(itemList);
        }
      });

      const customersRef = ref(database, "customers");
      onValue(customersRef, (snapshot) => {
        const data = snapshot.val();
        if (data) {
          const customerList = Object.entries(data).map(([id, customer]) => ({
            id,
            ...customer
          }));
          setCustomers(customerList);
        }
      });

      if (isEditMode) {
        await loadInvoiceForEdit();
      } else {
        const currentFY = settingsData.financialYear || getFinancialYear(new Date(invoiceDate));
        await loadExistingInvoiceNumbers(currentFY);
        await generateInvoiceNumber(currentFY, true);
      }
      
      setIsInitialized(true);
    };

    const loadInvoiceForEdit = async () => {
      try {
        const financialYearsRef = ref(database, "financialYears");
        const snapshot = await get(financialYearsRef);
        
        if (snapshot.exists()) {
          const years = snapshot.val();
          let foundInvoice = null;
          let foundYear = null;
          
          for (const [year, yearData] of Object.entries(years)) {
            if (yearData.bills && yearData.bills[id]) {
              foundInvoice = yearData.bills[id];
              foundYear = year;
              break;
            }
          }
          
          if (foundInvoice) {
            setInvoiceNo(foundInvoice.invoiceNo || "");
            setManualInvoiceNo(foundInvoice.invoiceNo || "");
            const match = foundInvoice.invoiceNo?.match(/\d+/);
            if (match) {
              setInvoiceNumber(parseInt(match[0], 10));
            }
            setInvoiceDate(foundInvoice.date || new Date().toISOString().split('T')[0]);
            setFinancialYear(foundYear || getFinancialYear(new Date(foundInvoice.date)));
            
            if (foundInvoice.customerId) {
              const customerData = {
                id: foundInvoice.customerId,
                name: foundInvoice.customerName || "",
                mobile: foundInvoice.customerMobile || "",
                gst: foundInvoice.customerGST || "",
                address: foundInvoice.customerAddress || "",
                state: foundInvoice.customerState || "",
                stateCode: foundInvoice.customerStateCode || ""
              };
              setSelectedCustomer(customerData);
              setCustomer(customerData.name);
              setCustomerId(customerData.id);
            }
            
            if (foundInvoice.items && Array.isArray(foundInvoice.items)) {
              setInvoiceItems(foundInvoice.items);
            }
            
            setNotes(foundInvoice.notes || "");
            setManualRoundOff(foundInvoice.manualRoundOff || null);
            await loadExistingInvoiceNumbers(foundYear || foundInvoice.financialYear);
          }
        }
      } catch (error) {
        console.error("Error loading invoice:", error);
        setError("Failed to load invoice");
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (isInitialized && !isEditMode && !settings.allowManualInvoiceNumber) {
      const currentFY = settings.financialYear || getFinancialYear(new Date(invoiceDate));
      generateInvoiceNumber(currentFY, true);
    }
  }, [invoiceDate, settings.financialYear, settings.invoicePrefix, settings.invoiceStartingNumber, isInitialized]);

  const totals = calculateTotals();
  const isIntraState = selectedCustomer?.state?.toLowerCase() === companyDetails.state?.toLowerCase();
  const displayFinancialYear = settings.financialYear || getFinancialYear(new Date(invoiceDate));

  return (
    <div className="invoice-page">
      {/* Header */}
      <div className="page-header">
        <h2>{isEditMode ? "Edit Invoice" : "Create New Invoice"}</h2>
        <div className="header-actions">
          <button onClick={() => navigate("/invoicelist")} className="back-btn">
            ← Invoices list
          </button>
          <button onClick={handlePreview} className="preview-btn" disabled={invoiceItems.length === 0 || !selectedCustomer}>
            👁️ Preview
          </button>
          <button onClick={handleDirectPrint} className="print-btn" disabled={invoiceItems.length === 0 || !selectedCustomer}>
            🖨️ Print
          </button>
        </div>
      </div>

      {/* Financial Year Bar */}
      <div className="financial-year-bar">
        <span className="fy-label">Financial Year:</span>
        <span className="fy-value">{financialYear || displayFinancialYear}</span>
      </div>

      {/* Round Off Toggle with Manual Input */}
      <div className="round-off-toggle">
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={roundOff}
            onChange={(e) => {
              setRoundOff(e.target.checked);
              if (!e.target.checked) setManualRoundOff(null);
            }}
          />
          <span className="toggle-slider"></span>
        </label>
        <span className="toggle-label">Enable Round Off</span>
        
        {roundOff && (
          <div className="round-off-input-group">
            <span className="round-off-note">
              {totals.grandTotal && (
                <span className="decimal-info">
                  Decimal: {(totals.grandTotal - Math.floor(totals.grandTotal)).toFixed(2)}
                  {totals.grandTotal - Math.floor(totals.grandTotal) <= 0.50 ? 
                    <span className="round-down"> → Round Down (Minus)</span> : 
                    <span className="round-up"> → Round Up (Plus)</span>}
                </span>
              )}
            </span>
            <input
              type="number"
              className="round-off-input"
              placeholder="Manual round off (+/-)"
              value={manualRoundOff !== null ? manualRoundOff : ""}
              onChange={(e) => handleManualRoundOffChange(e.target.value)}
              step="0.01"
            />
            <span className="round-off-hint">Enter custom round off value (e.g., +0.50 or -0.25)</span>
          </div>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <div className="error-alert">
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Main Content - Two Column Layout */}
      <div className="invoice-container">
        {/* LEFT COLUMN - FORM SECTION */}
        <div className="form-section">
          {/* Invoice Number Card */}
          <div className="card">
            <h3>Invoice Number</h3>
            {settings.allowManualInvoiceNumber ? (
              <div className="manual-invoice">
                <div className="input-group">
                  <input
                    type="text"
                    value={manualInvoiceNo}
                    onChange={handleManualInvoiceNoChange}
                    placeholder="Enter invoice number"
                    className={invoiceNumberError ? 'error' : ''}
                  />
                  <button onClick={applyManualInvoiceNo} className="apply-btn" disabled={!!invoiceNumberError}>
                    Apply
                  </button>
                </div>
                {invoiceNumberError && <p className="error-message">{invoiceNumberError}</p>}
                <p className="hint">Invoice numbers are unique per financial year. For {financialYear || displayFinancialYear}, numbers start from 1.</p>
              </div>
            ) : (
              <div className="auto-invoice">
                <div className="invoice-display">
                  <span className="prefix">{settings.invoicePrefix || 'INV-'}</span>
                  <input type="number" value={invoiceNumber} onChange={handleInvoiceNumberChange} min="1" step="1" />
                </div>
                <p className="hint">Next available number for {financialYear || displayFinancialYear}: {invoiceNumber}</p>
              </div>
            )}
          </div>

          {/* Date Card */}
          <div className="card">
            <h3>Invoice Date</h3>
            <input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} max={new Date().toISOString().split('T')[0]} className="date-input" />
          </div>

          {/* Customer Card */}
          <div className="card">
            <h3>Customer Details</h3>
            <div className="customer-selector">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by name, mobile or ID..."
                  value={customer}
                  onChange={(e) => {
                    setCustomer(e.target.value);
                    setShowCustomerDropdown(true);
                    setSelectedCustomer(null);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                />
                {selectedCustomer && (
                  <button className="clear-btn" onClick={() => {
                    setSelectedCustomer(null);
                    setCustomer("");
                    setCustomerId("");
                  }}>✕</button>
                )}
              </div>

              {showCustomerDropdown && customer && filteredCustomers.length > 0 && (
                <div className="dropdown">
                  {filteredCustomers.map(cust => (
                    <div key={cust.id} className="dropdown-item" onClick={() => selectCustomer(cust)}>
                      <div className="item-primary">
                        <strong>{cust.name}</strong>
                        <span className="mobile">{cust.mobile}</span>
                      </div>
                      <div className="item-secondary">
                        {cust.customerId && <span>ID: {cust.customerId}</span>}
                        {cust.state && <span>State: {cust.state}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {selectedCustomer && (
                <div className="selected-customer">
                  <div className="customer-header">
                    <span className="name">{selectedCustomer.name}</span>
                    {isIntraState ? (
                      <span className="gst-badge intra">Intra-state</span>
                    ) : (
                      <span className="gst-badge inter">Inter-state</span>
                    )}
                  </div>
                  <div className="customer-details">
                    <p>📱 {selectedCustomer.mobile || 'No mobile'}</p>
                    <p>🆔 {selectedCustomer.customerId}</p>
                    {selectedCustomer.gst && <p>GST: {selectedCustomer.gst}</p>}
                    {selectedCustomer.state && <p>State: {selectedCustomer.state}</p>}
                  </div>
                  <button className="change-btn" onClick={() => {
                    setSelectedCustomer(null);
                    setCustomer("");
                  }}>Change Customer</button>
                </div>
              )}
            </div>
          </div>

          {/* Add Items Card */}
          <div className="card">
            <h3>Add Items</h3>
            <div className="item-selector">
              <div className="search-row">
                <input
                  type="text"
                  placeholder="Search items by name, HSN or barcode..."
                  value={itemSearch}
                  onChange={(e) => {
                    setItemSearch(e.target.value);
                    setShowItemDropdown(true);
                  }}
                  onFocus={() => setShowItemDropdown(true)}
                />
                <button className="scan-btn" onClick={() => setShowScanner(true)} title="Scan Barcode">📷 Scan</button>
              </div>

              {showScanner && (
                <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setShowScanner(false)} useCamera={true} />
              )}

              {showItemDropdown && itemSearch && filteredItems.length > 0 && (
                <div className="item-dropdown">
                  {filteredItems.map(item => (
                    <div key={item.id} className="item-option" onClick={() => handleItemSelect(item)}>
                      <div className="item-header">
                        <strong>{item.name}</strong>
                        <span className="price">₹{Number(item.sellingPrice || item.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="item-details">
                        {item.hsn && <span>HSN: {item.hsn}</span>}
                        {item.gst > 0 && <span className="gst-badge">{item.gst}% GST</span>}
                        {item.barcode && <span>📷 {item.barcode}</span>}
                        {item.currentStock !== undefined && <span>📦 Stock: {item.currentStock}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showItemDropdown && itemSearch && filteredItems.length === 0 && (
                <div className="no-results">
                  <p>No items found matching "{itemSearch}"</p>
                  <button onClick={() => navigate('/items')}>Add New Item</button>
                </div>
              )}
            </div>
          </div>

          {/* Notes Card */}
          <div className="card">
            <h3>Notes (Optional)</h3>
            <textarea className="notes-input" placeholder="Add any additional notes here..." value={notes} onChange={(e) => setNotes(e.target.value)} rows="3" />
          </div>
        </div>

        {/* RIGHT COLUMN - INVOICE ITEMS */}
        <div className="items-section">
          <div className="card items-card">
            <h3>Invoice Items</h3>
            
            {invoiceItems.length > 0 ? (
              <>
                <div className="items-list">
                  {invoiceItems.map((item) => (
                    <div key={item.id} className="item-row">
                      <div className="item-row-header">
                        <div className="item-info">
                          <span className="item-name">{item.name}</span>
                          {item.hsn && <span className="item-hsn">HSN: {item.hsn}</span>}
                        </div>
                        <button className="remove-btn" onClick={() => removeItem(item.id)}>✕</button>
                      </div>
                      
                      <div className="item-row-details">
                        <div className="quantity-price">
                          <span>₹</span>
                          <input
                            type="number"
                            value={item.price}
                            onChange={(e) => updatePrice(item.id, parseFloat(e.target.value) || 0)}
                            className="price-input"
                            step="1"
                          />
                          <span> × </span>
                          <input
                            type="number"
                            min="1"
                            value={item.qty}
                            onChange={(e) => updateQuantity(item.id, parseFloat(e.target.value) || 1)}
                            className="qty-input"
                          />
                        </div>
                        
                        <div className="item-totals">
                          <input
                            type="number"
                            value={item.discount}
                            onChange={(e) => updateDiscount(item.id, parseFloat(e.target.value) || 0)}
                            className="discount-input"
                            placeholder="Disc %"
                            step="1"
                          />
                          <span className="discount-symbol">%</span>
                          {Number(item.gst) > 0 && (
                            <span className="gst-badge">{item.gst}%</span>
                          )}
                          <span className="net-total">₹{Number(item.netTotal).toFixed(2)}</span>
                        </div>
                      </div>

                      {Number(item.gst) > 0 && (
                        <div className="gst-breakup">
                          {isIntraState ? (
                            <>
                              <span>CGST: ₹{Number(item.gstDetails?.cgst).toFixed(2)}</span>
                              <span>SGST: ₹{Number(item.gstDetails?.sgst).toFixed(2)}</span>
                            </>
                          ) : (
                            <span>IGST: ₹{Number(item.gstDetails?.igst).toFixed(2)}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="invoice-summary">
                  <div className="summary-row">
                    <span>Taxable Value:</span>
                    <span>₹{totals.taxableValue.toFixed(2)}</span>
                  </div>
                  
                  {isIntraState ? (
                    <>
                      {totals.cgst > 0 && (
                        <div className="summary-row">
                          <span>CGST:</span>
                          <span>₹{totals.cgst.toFixed(2)}</span>
                        </div>
                      )}
                      {totals.sgst > 0 && (
                        <div className="summary-row">
                          <span>SGST:</span>
                          <span>₹{totals.sgst.toFixed(2)}</span>
                        </div>
                      )}
                    </>
                  ) : (
                    totals.igst > 0 && (
                      <div className="summary-row">
                        <span>IGST:</span>
                        <span>₹{totals.igst.toFixed(2)}</span>
                      </div>
                    )
                  )}
                  
                  {totals.totalDiscount > 0 && (
                    <div className="summary-row discount">
                      <span>Total Discount:</span>
                      <span>-₹{totals.totalDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="summary-row">
                    <span>Grand Total:</span>
                    <span>₹{totals.grandTotal.toFixed(2)}</span>
                  </div>
                  
                  {totals.showRoundOff && totals.roundOffAmount !== 0 && (
                    <>
                      <div className="summary-row round-off">
                        <span>Round Off:</span>
                        <span className={totals.roundOffAmount > 0 ? "round-up-value" : "round-down-value"}>
                          {totals.roundOffAmount > 0 ? '+' : ''}{totals.roundOffAmount.toFixed(2)}
                        </span>
                      </div>
                      <div className="summary-row grand-total">
                        <span>Rounded Total:</span>
                        <span>₹{totals.roundedGrandTotal.toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  
                  {!totals.showRoundOff && (
                    <div className="summary-row grand-total">
                      <span>Grand Total:</span>
                      <span>₹{totals.grandTotal.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="empty-items">
                <p>No items added yet</p>
                <p className="hint">Search and add items from the left panel</p>
              </div>
            )}

            <div className="action-buttons">
              <button 
                className="save-btn"
                onClick={saveInvoice}
                disabled={loading || !!invoiceNumberError || invoiceItems.length === 0 || !selectedCustomer}
              >
                {loading ? (
                  <>
                    <span className="spinner"></span>
                    Saving...
                  </>
                ) : (
                  isEditMode ? "💾 Update Invoice" : "💾 Save Invoice"
                )}
              </button>
              
              <button className="reset-btn" onClick={resetForm} disabled={loading}>
                🆕 New
              </button>
            </div>
          </div>
        </div>
      </div>
{/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      <style>{`
        .invoice-page {
          min-height: 100vh;
          background: #f5f5f5;
          padding-bottom: 70px;
        }

        .page-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 15px 20px;
          background: white;
          border-bottom: 1px solid #eee;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        
        .page-header h2 {
          margin: 0;
          font-size: 20px;
          color: #333;
        }

        .header-actions {
          display: flex;
          gap: 10px;
        }

        .back-btn, .print-btn {
          background: #f0f0f0;
          color: #333;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .print-btn {
          background: #28a745;
          color: white;
        }

        .preview-btn {
          background: #5a60ea;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 14px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .preview-btn:disabled, .print-btn:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .financial-year-bar {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 10px 20px;
          margin: 10px 20px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 14px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .round-off-toggle {
          background: white;
          margin: 0 20px 20px;
          padding: 15px 20px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .round-off-input-group {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .round-off-note {
          font-size: 12px;
          color: #666;
        }

        .decimal-info {
          background: #f5f5f5;
          padding: 4px 8px;
          border-radius: 6px;
        }

        .round-down {
          color: #dc3545;
        }

        .round-up {
          color: #28a745;
        }

        .round-off-input {
          padding: 8px 12px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 14px;
          width: 150px;
        }

        .round-off-hint {
          font-size: 11px;
          color: #999;
        }

        .round-off {
          color: #5a60ea;
        }

        .round-up-value {
          color: #28a745;
        }

        .round-down-value {
          color: #dc3545;
        }

        .toggle-label {
          font-size: 14px;
          color: #333;
          margin-left: 10px;
        }

        .fy-label { opacity: 0.9; font-weight: 500; }
        .fy-value { font-weight: bold; background: rgba(255,255,255,0.2); padding: 3px 12px; border-radius: 20px; }

        .error-alert {
          background: #ffebee;
          color: #c62828;
          padding: 10px 15px;
          margin: 0 20px 15px;
          border-radius: 8px;
          font-size: 13px;
          border-left: 3px solid #c62828;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .error-alert button { background: none; border: none; color: #c62828; cursor: pointer; font-size: 16px; }

        .invoice-container {
          display: grid;
          grid-template-columns: 1fr;
          gap: 20px;
          padding: 0 20px 20px;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (min-width: 1024px) {
          .invoice-container { grid-template-columns: 1fr 1.2fr; }
        }

        .card {
          background: white;
          border-radius: 12px;
          margin-bottom: 20px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .card h3 { margin: 0 0 15px 0; font-size: 16px; color: #333; font-weight: 600; }

        .input-group { display: flex; gap: 10px; }
        .input-group input { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
        .input-group input.error { border-color: #ff4444; }

        .apply-btn {
          padding: 12px 20px;
          background: #5a60ea;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          cursor: pointer;
          white-space: nowrap;
        }
        .apply-btn:disabled { background: #ccc; cursor: not-allowed; }

        .error-message { color: #ff4444; font-size: 12px; margin-top: 5px; }
        .hint { color: #666; font-size: 12px; margin-top: 8px; }

        .invoice-display {
          display: flex;
          align-items: center;
          background: #f5f5f5;
          border-radius: 8px;
          overflow: hidden;
        }
        .prefix { padding: 12px 15px; background: #e0e0e0; color: #333; font-weight: bold; font-size: 14px; }
        .invoice-display input { flex: 1; padding: 12px; border: none; background: #f5f5f5; font-size: 14px; outline: none; }

        .date-input { width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }

        .customer-selector { position: relative; }
        .search-box { position: relative; }
        .search-box input { width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
        .clear-btn { position: absolute; right: 10px; top: 50%; transform: translateY(-50%); background: none; border: none; color: #999; cursor: pointer; font-size: 16px; }

        .dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .dropdown-item { padding: 12px; border-bottom: 1px solid #eee; cursor: pointer; transition: background 0.2s; }
        .dropdown-item:hover { background: #f5f5f5; }
        .item-primary { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
        .mobile { color: #666; font-size: 12px; }
        .item-secondary { display: flex; gap: 15px; font-size: 11px; color: #888; }

        .selected-customer {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 15px;
          margin-top: 10px;
          border: 1px solid #e0e0e0;
        }
        .customer-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
        .name { font-weight: bold; font-size: 16px; }
        .gst-badge { padding: 2px 8px; border-radius: 12px; font-size: 11px; }
        .gst-badge.intra { background: #e3f2fd; color: #1976d2; }
        .gst-badge.inter { background: #fce4e4; color: #d32f2f; }
        .customer-details p { margin: 5px 0; font-size: 13px; color: #666; }
        .change-btn { background: #5a60ea; color: white; border: none; padding: 8px 15px; border-radius: 20px; font-size: 12px; cursor: pointer; margin-top: 10px; width: 100%; }

        .item-selector { position: relative; }
        .search-row { display: flex; gap: 10px; }
        .search-row input { flex: 1; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; }
        .scan-btn { padding: 12px 20px; background: #5a60ea; color: white; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; white-space: nowrap; }

        .item-dropdown {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          max-height: 300px;
          overflow-y: auto;
          background: white;
          border: 1px solid #ddd;
          border-radius: 8px;
          margin-top: 5px;
          z-index: 1000;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .item-option { padding: 12px; border-bottom: 1px solid #eee; cursor: pointer; transition: background 0.2s; }
        .item-option:hover { background: #f5f5f5; }
        .item-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
        .price { color: #5a60ea; font-weight: bold; }
        .item-details { display: flex; gap: 10px; font-size: 11px; color: #666; flex-wrap: wrap; }

        .no-results { padding: 20px; text-align: center; background: #f9f9f9; border-radius: 8px; margin-top: 10px; }
        .no-results button { background: none; border: none; color: #5a60ea; text-decoration: underline; cursor: pointer; margin-top: 10px; }

        .notes-input { width: 100%; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; resize: vertical; font-family: inherit; }

        .items-card { display: flex; flex-direction: column; height: fit-content; }
        .items-list { max-height: 400px; overflow-y: auto; margin-bottom: 15px; }
        @media (min-width: 1024px) { .items-list { max-height: 500px; } }

        .item-row { background: #f8f9fa; border-radius: 8px; padding: 15px; margin-bottom: 10px; border: 1px solid #eee; }
        .item-row-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
        .item-info { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .item-name { font-weight: bold; font-size: 14px; }
        .item-hsn { font-size: 11px; color: #666; background: #e0e0e0; padding: 2px 8px; border-radius: 12px; }
        .remove-btn { background: none; border: none; color: #ff4444; font-size: 16px; cursor: pointer; padding: 5px; }

        .item-row-details { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; flex-wrap: wrap; gap: 10px; }
        .quantity-price { display: flex; align-items: center; gap: 5px; flex-wrap: wrap; }
        .price-input, .qty-input { width: 70px; padding: 5px; border: 1px solid #ddd; border-radius: 4px; text-align: center; }
        .discount-input { width: 60px; padding: 5px; border: 1px solid #ddd; border-radius: 4px; text-align: center; }
        .discount-symbol { font-size: 12px; color: #666; }
        .item-totals { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
        .net-total { font-weight: bold; color: #5a60ea; font-size: 14px; }

        .gst-breakup { display: flex; gap: 10px; font-size: 11px; color: #666; margin-top: 8px; padding-top: 8px; border-top: 1px dashed #ddd; flex-wrap: wrap; }

        .invoice-summary { padding: 15px; background: #f8f9fa; border-radius: 8px; margin-top: 15px; }
        .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
        .summary-row.discount { color: #d32f2f; }
        .grand-total { font-weight: bold; font-size: 16px; color: #5a60ea; margin-top: 8px; padding-top: 8px; border-top: 1px solid #ddd; }

        .empty-items { text-align: center; padding: 40px 20px; color: #666; }
        .empty-items .hint { color: #999; font-size: 13px; margin-top: 10px; }

        .action-buttons { display: flex; gap: 10px; margin-top: 20px; }
        .save-btn { flex: 2; padding: 14px; background: linear-gradient(135deg, #5a60ea, #6f5bff); color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 5px; }
        .save-btn:disabled { opacity: 0.6; cursor: not-allowed; }
        .reset-btn { flex: 1; padding: 14px; background: #f0f0f0; color: #333; border: none; border-radius: 8px; font-size: 14px; font-weight: bold; cursor: pointer; }
        .reset-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid #fff; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }
        .bottom-nav div { text-align: center; cursor: pointer; color: #999; font-size: 20px; }
        .bottom-nav p { margin: 2px 0 0; font-size: 11px; }

        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: 0.3s;
          border-radius: 24px;
        }
        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 18px;
          width: 18px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.3s;
          border-radius: 50%;
        }
        input:checked + .toggle-slider { background-color: #5a60ea; }
        input:checked + .toggle-slider:before { transform: translateX(26px); }
      `}</style>
    </div>
  );
}